import Typography from '@/common/components/base/typography/Typography';
import BackwardIcon from '@/modules/Education/assets/icons/backward.svg?react';
import ForwardIcon from '@/modules/Education/assets/icons/forward.svg?react';
import PauseIcon from '@/modules/Education/assets/icons/pause.svg?react';
import PlayIcon from '@/modules/Education/assets/icons/play.svg?react';
import { useEffect, useRef, useState } from 'react';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';
import { VoicePlayerProps } from '../../types/Education.type';
import { formatTime } from '../../utils/formatTime';

const BAR_COUNT = 96;
const WAVE_HEIGHTS = [
  8, 12, 18, 24, 30, 35, 31, 27, 20, 14, 18, 23, 29, 35, 31, 25, 19, 15, 20, 27,
  33, 37, 34, 28, 22, 18, 23, 29, 35, 38, 34, 29, 24, 20, 24, 29, 34, 38, 35,
  30, 25, 21, 24, 28, 33, 37, 34, 29, 24, 20, 23, 28, 33, 37, 35, 30, 25, 20,
  22, 27, 32, 36, 34, 29, 24, 20, 22, 27, 31, 35, 33, 28, 23, 19, 22, 27, 31,
  34, 31, 27, 22, 18, 21, 26, 30, 33, 30, 25, 20, 16, 19, 23, 27, 30, 27, 22,
];

export default function VoicePlayer({
  src,
  autoPlay = false,
}: VoicePlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);

    const onLoadedMetadata = async () => {
      setDuration(audio.duration);

      if (autoPlay) {
        try {
          await audio.play();
          setIsPlaying(true);
        } catch (err) {
          console.error(err);
        }
      }
    };
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying((prev) => !prev);
  };

  const handleForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.duration,
        audioRef.current.currentTime + 10,
      );
    }
  };

  const handleBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        0,
        audioRef.current.currentTime - 10,
      );
    }
  };

  const handleBarClick = (index: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const newTime = (index / BAR_COUNT) * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const progress = duration > 0 ? currentTime / duration : 0;
  const filledBars = Math.floor(progress * BAR_COUNT);

  return (
    <Box
      bgColor="#4A357F80"
      p="12px 14px"
      display="flex"
      flexDirection="column"
    >
      <audio ref={audioRef} src={src} preload="metadata">
        <track kind="captions" srcLang="fa" label="Persian" />
      </audio>

      <Flex flexDirection="column" gap="10px">
        <Flex
          alignItems="center"
          justifyContent="center"
          dir="ltr"
          width="100%"
          gap="2px"
        >
          {Array.from({ length: BAR_COUNT }).map((_, i) => {
            const filled = i < filledBars;
            const height = WAVE_HEIGHTS[i % WAVE_HEIGHTS.length];

            return (
              <Box
                key={i}
                onClick={() => handleBarClick(i)}
                width="2px"
                height={`${height}px`}
                minHeight="6px"
                borderRadius="rounded"
                bgColor={filled ? '#5534AF' : '#FFF'}
                cursor="pointer"
                flexShrink={0}
                transition="background-color .2s"
              />
            );
          })}
        </Flex>
        <Flex
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          dir="ltr"
        >
          <Box minWidth="48px">
            <Typography
              textStyle="b1"
              color="#FFF"
              className={css({ fontVariantNumeric: 'tabular-nums' })}
            >
              {formatTime(currentTime)}
            </Typography>
          </Box>

          <Flex flex="1" justifyContent="center" alignItems="center" gap={12}>
            <Flex
              onClick={handleBackward}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              flexShrink={0}
            >
              <BackwardIcon />
            </Flex>

            <Flex
              onClick={togglePlay}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              flexShrink={0}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </Flex>

            <Flex
              onClick={handleForward}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              flexShrink={0}
            >
              <ForwardIcon />
            </Flex>
          </Flex>

          <Box minWidth="48px" textAlign="right">
            <Typography
              textStyle="b1"
              color="#FFF"
              className={css({ fontVariantNumeric: 'tabular-nums' })}
            >
              {formatTime(duration)}
            </Typography>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
}
